/* eslint-disable formatjs/no-literal-string-in-jsx */
import { EmptyState, ErrorState, TableSkeletonLoader } from "@repo/ui";
import { Loader, TableSortable, Typography } from "@saviynt/design-system";
import { useCallback, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { getSortConfigs, getUsersTableColumns } from "./UserTableConfig";
import ActionButton from "./components/CellRenderers/ActionButton";
import { UsersTableData } from "./types";
import { useInfiniteScroll } from "../../hooks/useInfiniteScroll";
import { useUsersTable } from "./hooks/useUsersTable";
import { useSelection } from "../../contexts/SelectionContext";
import { formatTimestamp } from "../../utils/timeUtils";
import { getRoleDisplayName } from "../../utils/roleDisplayName";
import { Status } from "./components/CellRenderers/Status/Status";

export type UsersTableProps = {
  searchValue: string;
  statusFilter: string;
  roleFilter: string;
};

export default function UsersTable({
  searchValue,
  statusFilter,
  roleFilter,
}: UsersTableProps) {
  const intl = useIntl();

  const [selectedRowIndex, setSelectedRowIndex] = useState<
    number | undefined
  >();
  const { selectedRows, setSelectedRows } = useSelection();

  const [columns, setColumns] = useState(() => getUsersTableColumns(intl));
  const [sortState, setSortState] = useState<{
    sortBy?: string;
    sortOrder?: string;
  }>({});
  /**
   * Use Virtuoso's own scroll area: window-based virtualization only mounts a
   * small slice of rows. Pointing at the app main scroll parent is brittle; a
   * fixed table height with isWindowScroll false lists all items when scrolling
   * inside the table.
   */
  const tableViewportHeight = "min(72vh, calc(100dvh - 12rem))";

  const {
    data,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isLoading,
    error,
  } = useUsersTable({
    pageSize: 20,
    search: searchValue || undefined,
    status: statusFilter || undefined,
    role: roleFilter || undefined,
    ...sortState,
  });

  const observerRef = useInfiniteScroll({
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  });

  const usersData = useMemo(
    () => data?.pages.flatMap((page) => page.content) || [],
    [data],
  );

  const handleRowClick = useCallback((_row: unknown, rowIndex: number) => {
    setSelectedRowIndex(rowIndex);
  }, []);

  const createTableRow = (user: UsersTableData) => {
    const baseRow: Record<string, React.ReactNode> = {
      id: user.userId,
      name: (
        <Typography kind="body2" color="--color-foreground-secondary-bold">
          {`${user.firstName} ${user.lastName}`}
        </Typography>
      ),
      savRole: (
        <Typography kind="body2" color="--color-foreground-secondary-bold">
          {user.roles?.map(getRoleDisplayName).join(", ")}
        </Typography>
      ),
      lastAccessedAt: (
        <Typography kind="body2" color="--color-foreground-secondary-bold">
          {user.lastAccessedAt
            ? formatTimestamp(user.lastAccessedAt)
            : undefined}
        </Typography>
      ),
      createdBy: (
        <Typography kind="body2" color="--color-foreground-secondary-bold">
          {user.createdByName}
        </Typography>
      ),
      createdAt: (
        <Typography kind="body2" color="--color-foreground-secondary-bold">
          {user.createdAt ? formatTimestamp(user.createdAt) : undefined}
        </Typography>
      ),
      status: (
        <Typography kind="body2" color="--color-foreground-secondary-bold">
          <Status text={user.status} />
        </Typography>
      ),
      actionColumn: (
        <ActionButton
          id={user.userId}
          userEmail={user.email}
          userStatus={user.status}
        />
      ),
    };
    // Add dynamic fallback fields using Typography
    const knownKeys = new Set(Object.keys(baseRow));

    const dynamicFields = Object.keys(user).reduce<
      Record<string, React.ReactNode>
    >((acc, key) => {
      if (knownKeys.has(key)) {
        return acc;
      }

      const value = user[key as keyof UsersTableData];

      acc[key] = (
        <Typography
          kind="body2"
          color="--color-foreground-secondary-bold"
          lineLimit={1}
        >
          {value != null ? String(value) : "-"}
        </Typography>
      );

      return acc;
    }, {});

    return {
      ...baseRow,
      ...dynamicFields,
    };
  };

  const tableRows = usersData.map(createTableRow);

  const handleColumnSort = useCallback((columnKey: string, order: string) => {
    setSortState({
      sortBy: columnKey,
      sortOrder: order.toUpperCase(),
    });
  }, []);

  if (isLoading) {
    return <TableSkeletonLoader rows={15} />;
  }

  return (
    <>
      {error ? (
        <ErrorState
          message={intl.formatMessage({
            id: "TRZd7+",
            defaultMessage: "Failed to load users",
            description: "Error message when roles fail to load",
          })}
        />
      ) : tableRows.length === 0 ? (
        <EmptyState
          message={intl.formatMessage({
            id: "ZNiCrn",
            defaultMessage: "No user found",
            description: "Empty state message when no users exist",
          })}
        />
      ) : (
        <div className="w-full min-w-0 max-w-full mt-1">
          <div className="min-h-0 min-w-0 overflow-x-auto">
            <TableSortable
              columnConfigs={{ isDndDisabled: true }}
              columns={columns}
              setColumns={setColumns}
              sortConfigs={getSortConfigs(intl)}
              onSort={handleColumnSort}
              data={tableRows}
              isWindowScroll={false}
              tableHeight={tableViewportHeight}
              selectedRowIndex={selectedRowIndex}
              onRowClick={handleRowClick}
              selectedRows={selectedRows}
              onRowSelect={setSelectedRows}
              className="Users-Table"
              dataTestId="users-Table"
            />
          </div>
        </div>
      )}

      {isFetchingNextPage && !isLoading && (
        <div
          className="flex justify-center py-2 items-center gap-2"
          data-testid="loader-loop"
          role="status"
          aria-live="polite"
          aria-label={intl.formatMessage({
            defaultMessage: "Loading more items",
            description: "Loading more items",
            id: "E9ii8a",
          })}
        >
          <Loader kind="loop" format="inline" color="brand" size="medium" />
        </div>
      )}
      <div ref={observerRef} style={{ height: "1px" }} aria-hidden="true" />
    </>
  );
}
